import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { legacyNav } from '@/iframe/legacyNavState';
import { activeMenuView, buildLegacyMenu, chromeModeForRoute } from '@/utils/domain/legacyNavModel';

export const RAIL_WIDTH = 56;

// Top bar + side rail only when the iframe keeps at least the legacy desktop width
// (nav.less @laptop-breakpoint, 1025px) next to the rail: the legacy media queries and
// windowHelper thresholds measure the iframe, so a narrower one would switch the legacy pages
// to their mobile layout under a desktop chrome. Below, the bottom navigation takes over.
const DESKTOP_QUERY = `(min-width: ${1025 + RAIL_WIDTH}px)`;
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

const mediaRef = (query) => {
	const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null;
	const value = ref(Boolean(mq?.matches));
	mq?.addEventListener?.('change', (event) => (value.value = event.matches));
	return value;
};

let layout = null;

// Safe-area insets are CSS-only (env()); VAppBar / VBottomNavigation reserve layout space from
// their `height` prop, so the insets are measured once through a probe element and kept in sync.
function measureSafeArea(target) {
	const probe = document.createElement('div');
	probe.setAttribute('aria-hidden', 'true');
	probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;top:0;left:0;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);';
	document.body.appendChild(probe);

	const update = () => {
		const style = getComputedStyle(probe);
		target.top = parseFloat(style.paddingTop) || 0;
		target.bottom = parseFloat(style.paddingBottom) || 0;
	};
	update();
	window.addEventListener('resize', update);
	window.addEventListener('orientationchange', update);
}

function getLayout() {
	if (layout) return layout;
	const safeArea = reactive({ top: 0, bottom: 0 });
	if (typeof document !== 'undefined') measureSafeArea(safeArea);
	layout = { desktop: mediaRef(DESKTOP_QUERY), canHover: mediaRef(HOVER_QUERY), safeArea };
	return layout;
}

export function useAppChrome() {
	const route = useRoute();
	const { desktop, canHover, safeArea } = getLayout();

	const mode = computed(() => chromeModeForRoute(legacyNav.chromeMode, { portal: Boolean(route.meta?.portal), legacy: Boolean(route.meta?.legacyView) }));
	const visible = computed(() => mode.value !== 'hidden');
	const onApp = computed(() => mode.value === 'app');

	const menu = computed(() => buildLegacyMenu({ onApp: onApp.value, menus: legacyNav.menus, flags: legacyNav.flags, shortcuts: legacyNav.shortcuts }));
	const activeView = computed(() => activeMenuView(route.meta?.legacyView ?? null));

	const visitsCount = computed(() => legacyNav.visits.current.length + legacyNav.visits.preparation.length + legacyNav.visits.late.length);
	const counts = computed(() => ({ cart: legacyNav.cart.count, visits: visitsCount.value, notifications: legacyNav.notifications.length }));

	// Visits only exist for SFA customers (sidebar.js evtAppSwitching).
	const panels = computed(() => (onApp.value ? [...(legacyNav.sfaActivated ? ['visits'] : []), 'cart', 'notifications'] : []));

	const userName = computed(() => [legacyNav.user.firstName, legacyNav.user.lastName].filter(Boolean).join(' '));
	// Environment colours of the legacy bars (nav.less .dev-navbar / .qa-navbar).
	const color = computed(() => ({ DEV: '#137c50', QA: '#158d83' })[legacyNav.env] ?? 'primary');

	return { nav: legacyNav, mode, visible, onApp, desktop, canHover, safeArea, menu, activeView, counts, panels, userName, color };
}
